<template>
    <div class="single__filters">
        <div v-if="categories.length" class="single__filters__dropdown">
            <p>
                {{ i18n.filterBy }}
            </p>
            <ResourceSelect
                id="news-category-filter"
                :model-value="category"
                :terms="categories"
                :label="i18n.selectCategory"
                :all-label="i18n.allCategories"
                :clear-label="i18n.clearCategory"
                :icons="icons"
                @update:model-value="$emit('update:category', $event)"
            />
            <ResourceSelect
                v-if="subcategories.length"
                :key="category || 'all'"
                id="news-subcategory-filter"
                :model-value="subcategory"
                :terms="subcategories"
                :label="i18n.selectSubcategory"
                :all-label="i18n.allSubcategories"
                :clear-label="i18n.clearSubcategory"
                :icons="icons"
                @update:model-value="$emit('update:subcategory', $event)"
            />
        </div>
        <div class="page404__search">
            <form class="search__form" :action="pageUrl" method="get" @submit.prevent="submit">
                <input v-if="category" type="hidden" name="news_category" :value="category">
                <input v-if="subcategory" type="hidden" name="news_subcategory" :value="subcategory">
                <button type="submit" v-html="submitContent"></button>
                <label class="vh" for="news-search">{{ i18n.search }}</label>
                <input
                    id="news-search"
                    v-model="term"
                    type="search"
                    name="news_search"
                    :placeholder="i18n.search"
                >
            </form>
        </div>
        <a
            v-if="hasFilters"
            class="single__filters__reset"
            :href="pageUrl"
            @click.prevent="onReset"
        >
            <span v-html="icons.clearAll"></span>
            {{ i18n.resetFilters }}
        </a>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import debounce from 'lodash/debounce'

import ResourceSelect from '../resources/ResourceSelect.vue'

const props = defineProps({
    category: { type: String, default: '' },
    subcategory: { type: String, default: '' },
    search: { type: String, default: '' },
    categories: { type: Array, default: () => [] },
    hasFilters: { type: Boolean, default: false },
    pageUrl: { type: String, default: '' },
    icons: { type: Object, default: () => ({}) },
    i18n: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['update:category', 'update:subcategory', 'update:search', 'reset'])

const subcategories = computed(() => {
    if (!props.category) {
        return props.categories
            .flatMap((parent) => parent.children || [])
            .sort((a, b) => a.name.localeCompare(b.name))
    }

    const parent = props.categories.find((item) => item.slug === props.category)

    return parent?.children || []
})

const focusFilter = (id) => {
    const field = document.getElementById(id)

    if (!field) {
        return
    }

    const selection = field.parentElement?.querySelector('.select2-selection')

    if (selection instanceof HTMLElement) {
        selection.focus()
        return
    }

    field.focus()
}

const onReset = () => {
    emit('reset')
    nextTick(() => focusFilter('news-category-filter'))
}

const term = ref(props.search)

const submitContent = computed(
    () => `<div class="vh">${props.i18n.search || ''}</div>${props.icons.search || ''}`
)

const normalized = (value) => (value || '').trim().replace(/\s+/g, ' ')

const emitSearch = debounce(() => {
    const next = normalized(term.value)

    if (next !== props.search) {
        emit('update:search', next)
    }
}, 400)

watch(term, emitSearch)

watch(() => props.search, (value) => {
    if (value !== normalized(term.value)) {
        emitSearch.cancel()
        term.value = value
    }
})

const submit = () => {
    emitSearch.cancel()

    const next = normalized(term.value)

    if (next !== props.search) {
        emit('update:search', next)
    }
}

onBeforeUnmount(() => emitSearch.cancel())
</script>
